'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, CheckCheck, Key, UserCheck, Building2, AlertTriangle, Info, UserMinus, RefreshCw } from 'lucide-react';

export interface LiveNotification {
  id: string;
  type: 'license_assigned' | 'employee_activated' | 'org_setup_complete' | 'system_alert' | 'license_revoked' | 'employee_suspended';
  title: string;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'danger';
  timestamp: string;
  read: boolean;
  meta?: Record<string, string | number>;
}

interface NotificationCenterProps {
  scope?: 'control' | 'admin';
  organizationId?: number;
  pollIntervalMs?: number;
}

const TYPE_ICON: Record<LiveNotification['type'], React.ElementType> = {
  license_assigned: Key,
  license_revoked: Key,
  employee_activated: UserCheck,
  employee_suspended: UserMinus,
  org_setup_complete: Building2,
  system_alert: AlertTriangle,
};

const SEVERITY_STYLES: Record<LiveNotification['severity'], { dot: string; icon: string; bg: string; border: string }> = {
  success: { dot: 'bg-success', icon: 'text-success', bg: 'bg-success/10', border: 'border-success/20' },
  info: { dot: 'bg-info', icon: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
  warning: { dot: 'bg-warning', icon: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' },
  danger: { dot: 'bg-danger', icon: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/20' },
};

function timeAgo(isoString: string): string {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationCenter({
  scope = 'control',
  organizationId,
  pollIntervalMs = 30000,
}: NotificationCenterProps) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<LiveNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [pulse, setPulse] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const prevUnreadRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams({ scope });
      if (organizationId) params.set('organizationId', String(organizationId));
      const res = await fetch(`/api/notifications/live?${params}`);
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      const newUnread: number = data.unreadCount || 0;
      if (newUnread > prevUnreadRef.current) {
        setPulse(true);
        setTimeout(() => setPulse(false), 1500);
      }
      prevUnreadRef.current = newUnread;
      setUnreadCount(newUnread);
    } catch {
      // silent fail
    }
  }, [scope, organizationId]);

  useEffect(() => {
    setLoading(true);
    fetchNotifications().finally(() => setLoading(false));
    const interval = setInterval(fetchNotifications, pollIntervalMs);
    return () => clearInterval(interval);
  }, [fetchNotifications, pollIntervalMs]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await fetch('/api/notifications/live', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markAll: true }),
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    } catch {
      // silent fail
    }
  };

  const markRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const displayed = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(p => !p)}
        className={`relative p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${open ? 'bg-muted text-foreground' : ''}`}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell size={16} className={pulse ? 'animate-bounce' : ''} />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-danger text-white text-[9px] font-700 leading-none transition-transform ${pulse ? 'scale-125' : 'scale-100'}`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-1rem)] bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-foreground" />
              <span className="font-700 text-sm text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-700 px-1.5 py-0.5 rounded-full bg-danger text-white leading-none">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={fetchNotifications}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                title="Refresh"
              >
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-600 text-primary hover:bg-primary/10 transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck size={12} />
                  All read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              >
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex border-b border-border">
            {(['all', 'unread'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-1 py-2 text-[11px] font-600 transition-colors capitalize ${filter === f ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                {f === 'unread' ? `Unread (${unreadCount})` : `All (${notifications.length})`}
              </button>
            ))}
          </div>

          {/* Notification List */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border">
            {displayed.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Bell size={28} className="text-muted-foreground/40 mb-2" />
                <p className="text-sm font-500 text-muted-foreground">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
              </div>
            ) : (
              displayed.map(notif => {
                const IconComp = TYPE_ICON[notif.type] || Info;
                const styles = SEVERITY_STYLES[notif.severity];
                return (
                  <div
                    key={notif.id}
                    onClick={() => !notif.read && markRead(notif.id)}
                    className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${!notif.read ? 'bg-primary/[0.03] hover:bg-primary/[0.06]' : 'hover:bg-muted/50'}`}
                  >
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${styles.bg} ${styles.border}`}>
                      <IconComp size={14} className={styles.icon} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[12px] font-${notif.read ? '500' : '700'} text-foreground leading-snug`}>
                          {notif.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">
                          {timeAgo(notif.timestamp)}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                        {notif.message}
                      </p>
                    </div>

                    {/* Unread dot */}
                    {!notif.read && (
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${styles.dot}`} />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground text-center">
              Auto-refreshes every {Math.round(pollIntervalMs / 1000)}s · {notifications.length} total events
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
